import React from 'react';
import {
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
} from '@floating-ui/react';

const SHORTCUTS = [
  { keys: ['g', 'c'], label: 'Go to Actuals — the closed books' },
  { keys: ['g', 'm'], label: 'Go to Budgets — plan + operational pulse' },
  { keys: ['g', 'f'], label: 'Go to Forecasts — project forward' },
  { keys: ['g', 'u'], label: 'Go to Support Stack — sub-ledgers, schedules, trackers, intents' },
  { keys: ['g', 's'], label: 'Go to Synthesis' },
  { keys: ['g', 'b'], label: 'Go to Command Module — simulation workspace' },
  { keys: ['g', 'd'], label: 'Go to Drivers — your parameter data' },
  { keys: ['g', 't'], label: 'Go to Settings — app behavior' },
  { keys: ['?'],      label: 'Show this help' },
  { keys: ['Esc'],    label: 'Close dialog / menu' },
  { keys: ['['],      label: 'Toggle sidebar collapse' },
];

const KBD = 'font-mono bg-surface-raised border border-border-strong border-b-2 rounded-[4px] px-[7px] py-px text-[0.78rem] text-text';

/* v8 — ShortcutHelp uses Floating UI's FloatingFocusManager for proper
   focus trapping (Tab cycles within dialog) + focus restoration on close.
   `useDismiss` handles Escape and outside-click. */
export default function ShortcutHelp({ open, onClose }) {
  const { refs, context } = useFloating({
    open,
    onOpenChange: (o) => { if (!o) onClose(); },
  });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay
        lockScroll
        className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/55 px-4 py-4 backdrop-blur-[4px] sm:py-8"
      >
        <FloatingFocusManager context={context}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            aria-label="Keyboard shortcuts"
            className="flex w-full min-w-0 max-w-[620px] max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border border-border-strong bg-surface-el shadow-lg outline-none"
          >
            <header className="flex shrink-0 items-center justify-between gap-3 px-[1.1rem] py-[0.85rem] border-b border-border">
              <h3 className="text-base font-semibold text-text m-0">Keyboard shortcuts</h3>
              <button
                type="button"
                className="bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft"
                onClick={onClose}
                aria-label="Close"
              >✕</button>
            </header>
            <div className="min-h-0 overflow-y-auto overscroll-contain px-[1.1rem] pt-4 pb-[1.2rem]">
              <ul className="list-none p-0 m-0">
                {SHORTCUTS.map((s, i) => (
                  <li key={i} className="flex items-center gap-[0.85rem] py-[0.4rem] border-b border-dashed border-border last:border-b-0">
                    <span className="flex gap-1 min-w-[80px]">
                      {s.keys.map((k, j) => (
                        <kbd key={j} className={KBD}>{k}</kbd>
                      ))}
                    </span>
                    <span className="text-text-muted text-[0.85rem]">{s.label}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm">
                Chord shortcuts (e.g. <kbd className={KBD}>g</kbd> then <kbd className={KBD}>c</kbd>) — press in sequence within ~1.2s.
              </p>
            </div>
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
